// Shared constants and helpers for Equipment filtering.
// Used by EquipmentLanding, EquipmentDetail, and EquipmentFilterSidebar.

export const CATEGORY_OPTIONS = ['All', 'Boom Lift', 'Scissor Lift', 'Telehandler', 'Fork Lift'];

export const EQUIPMENT_TYPES_BY_CATEGORY = {
  All: [],
  'Boom Lift': ['Telescopic Boom Lift', 'Articulating Boom Lift'],
  'Scissor Lift': ['Electric Scissor Lift', 'Rough Terrain Scissor Lift', 'Vertical Mast Lift'],
  Telehandler: [],
  'Fork Lift': ['Pneumatic Fork Lift'],
};

export const RANGE_BUCKETS = [
  { label: "12' - 15'", min: 12, max: 15 },
  { label: "18' - 20'", min: 18, max: 20 },
  { label: "20' - 30'", min: 20, max: 30 },
  { label: "30' - 35'", min: 30, max: 35 },
  { label: "40' - 45'", min: 40, max: 45 },
  { label: "55' - 65'", min: 55, max: 65 },
  { label: "80' - 86'", min: 80, max: 86 },
];

export const POUNDS_OPTIONS = [
  { label: '5,000 lbs +', min: 5000 },
  { label: '10,000 lbs +', min: 10000 },
];

// Extract the primary height (in feet) from the equipment name
export const getHeightFromName = (name) => {
  const matches = [...name.matchAll(/(\d+)\s*'/g)].map((m) => parseInt(m[1], 10));
  if (matches.length === 0) return null;
  return Math.max(...matches);
};

// Extract pounds capacity from equipment name (e.g., "5,000lbs" or "10,000lb")
export const getPoundsFromName = (name) => {
  const match = name.match(/(\d{1,3}(?:,\d{3})+|\d+)\s*lb/i);
  if (!match) return null;
  return parseInt(match[1].replace(/,/g, ''), 10);
};

// Given a category + equipmentType, return the pool of items that can be matched
// (used to compute which Range/Pounds buckets are selectable).
export const getCategoryFilteredPool = (items, { selectedCategory, selectedEquipmentType }) => {
  let filtered = [...items];
  if (selectedCategory !== 'All') {
    filtered = filtered.filter((item) => item.category === selectedCategory);
  }
  if (selectedEquipmentType !== 'All') {
    filtered = filtered.filter((item) => item.equipmentType === selectedEquipmentType);
  }
  return filtered;
};

export const getAvailableRanges = (items, filters) => {
  const pool = getCategoryFilteredPool(items, filters);
  const available = new Set();
  pool.forEach((item) => {
    const height = getHeightFromName(item.name);
    if (height === null) return;
    RANGE_BUCKETS.forEach((bucket) => {
      if (height >= bucket.min && height <= bucket.max) available.add(bucket.label);
    });
  });
  return RANGE_BUCKETS.filter((b) => available.has(b.label));
};

export const getAvailablePounds = (items, filters) => {
  const pool = getCategoryFilteredPool(items, filters);
  const available = new Set();
  pool.forEach((item) => {
    const pounds = getPoundsFromName(item.name);
    if (pounds === null) return;
    POUNDS_OPTIONS.forEach((opt) => {
      if (pounds >= opt.min) available.add(opt.label);
    });
  });
  return POUNDS_OPTIONS.filter((o) => available.has(o.label));
};

// Apply all filters to the full list of items.
export const applyFilters = (items, filters) => {
  const {
    searchQuery,
    selectedCategory,
    selectedEquipmentType,
    selectedRange,
    selectedPounds,
    maxPrice,
    priceTimeframe,
  } = filters;

  let filtered = [...items];

  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        (item.description && item.description.toLowerCase().includes(q))
    );
  }

  if (selectedCategory !== 'All') {
    filtered = filtered.filter((item) => item.category === selectedCategory);
  }

  if (selectedEquipmentType !== 'All') {
    filtered = filtered.filter((item) => item.equipmentType === selectedEquipmentType);
  }

  if (selectedRange !== 'All') {
    const bucket = RANGE_BUCKETS.find((b) => b.label === selectedRange);
    if (bucket) {
      filtered = filtered.filter((item) => {
        const h = getHeightFromName(item.name);
        return h !== null && h >= bucket.min && h <= bucket.max;
      });
    }
  }

  if (selectedPounds !== 'All') {
    const opt = POUNDS_OPTIONS.find((o) => o.label === selectedPounds);
    if (opt) {
      filtered = filtered.filter((item) => {
        const p = getPoundsFromName(item.name);
        return p !== null && p >= opt.min;
      });
    }
  }

  if (maxPrice) {
    const priceValue = parseFloat(maxPrice);
    if (!Number.isNaN(priceValue)) {
      filtered = filtered.filter((item) => {
        const price = item.pricing?.[priceTimeframe] || 0;
        return price <= priceValue;
      });
    }
  }

  return filtered;
};

export const hasActiveFilters = (filters) =>
  !!(
    filters.searchQuery ||
    filters.selectedCategory !== 'All' ||
    filters.selectedEquipmentType !== 'All' ||
    filters.selectedRange !== 'All' ||
    filters.selectedPounds !== 'All' ||
    filters.maxPrice
  );
